import { Chip, makeStyles } from "@material-ui/core";
import { CloudDone, CloudOff } from "@material-ui/icons";
import React, { useContext } from "react";

import { SiteAggregate } from "../../../dtos/siteAggregate";
import { ActiveStatus } from "../../../enums/ActiveStatus";
import { TimeZone, TIME_ZONE_LABELS } from "../../../enums/timeZone";
import { useAuth } from "../../../features/auth/context";
import SignalRContext from "../../../signalr/context";
import { NotesEditField } from "../../shared/NotesEditField";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";
import { useApi } from "../../useApi";

import { MaintenanceModeBadge } from "../../shared/MaintenanceModeBadge";
import { SiteRowActions } from "./SiteRowActions";

interface Props {
    sites: SiteAggregate[];
    sitesAreLoading: boolean;
    contextDealerId: number | undefined;
    contextOrgId: number | undefined;
    onDeleteSite: (id: number) => void;
    onLoadSites: () => void;
}

// Pull the GMT offset out of the verbose label so the table shows just
// "GMT-06:00" instead of "(GMT-06:00) Central Time (US & Canada)". The
// full label stays available as a title attribute for hover.
const compactTimeZone = (tz: TimeZone | undefined): { short: string; full: string } => {
    const full = TIME_ZONE_LABELS[tz as TimeZone] ?? "";
    const match = full.match(/^\(([^)]+)\)\s*(.*)$/);
    return match ? { short: match[1], full } : { short: full, full };
};

export function SitesTable({
    sites,
    sitesAreLoading,
    contextDealerId,
    contextOrgId,
    onDeleteSite,
    onLoadSites,
}: Props) {
    const classes = useStyles();
    const { siteApi } = useApi();
    const { serviceStatus, maintenanceModeStatus } = useContext(SignalRContext);
    const {
        isGlobalAdmin,
        isSiteEmployee,
        isDealerAdmin,
        isOrgAdmin,
        siteId,
        isReadOnlyUser,
    } = useAuth();

    const showDeleteButton = isGlobalAdmin;
    const showActivationButton = isGlobalAdmin || isDealerAdmin || isOrgAdmin;
    const showNotesEdit = isGlobalAdmin || isDealerAdmin;
    const showMaintenanceMode = isDealerAdmin;

    const columns: Column<SiteAggregate>[] = [
        { key: 'uniqueSiteId', label: 'Id' },
        { key: 'name', label: 'Name', primary: true },
        {
            key: 'status',
            label: 'Status',
            render: (s) => (serviceStatus.get(s.id) ? <CloudDone /> : <CloudOff />),
        },
        ...(showMaintenanceMode
            ? [{
                key: 'maintenance',
                label: 'Maintenance',
                hideWhenEmpty: true,
                render: (s: SiteAggregate) => {
                    const ms = maintenanceModeStatus.get(s.id);
                    if (ms?.isEnabled && ms?.endTime) {
                        return <MaintenanceModeBadge endTime={ms.endTime} />;
                    }
                    return null;
                },
            } as Column<SiteAggregate>]
            : []),
        { key: 'totalNumberOfBoxes', label: 'Total' },
        { key: 'totalNumberOfBoxesUsed', label: 'Used' },
        {
            key: 'boxTypeAvailability',
            label: 'Box Types',
            render: (s) =>
                s.boxTypeAvailability.length === 0
                    ? null
                    : (
                        <div className={classes.chipsRow}>
                            {s.boxTypeAvailability.map((a) => (
                                <Chip
                                    key={a.size}
                                    size="small"
                                    label={`${a.size}: ${a.count}`}
                                    className={classes.boxChip}
                                />
                            ))}
                        </div>
                    ),
        },
        { key: 'referenceSiteId', label: 'Reference', hideWhenEmpty: true },
        {
            key: 'timeZone',
            label: 'TZ',
            render: (s) => {
                const { short, full } = compactTimeZone(s.timeZone as TimeZone);
                return <span title={full}>{short}</span>;
            },
        },
        ...(showNotesEdit
            ? [{
                key: 'notes',
                label: 'Notes',
                render: (s: SiteAggregate) => (
                    <NotesEditField
                        notes={s.notes}
                        updateNotesRequest={siteApi.updateSiteNotes}
                        entityId={s.id}
                        tooltipText=""
                        onNotesUpdated={onLoadSites}
                        disabled={false}
                    />
                ),
            } as Column<SiteAggregate>]
            : []),
    ];

    // [PAS-1003] The previous gate (`!isSiteEmployee && siteId === undefined`)
    // excluded Site Admins, who are always scoped to a single siteId, so the
    // entire Actions column never rendered for them and they lost access to
    // Configure on their own site. Site Admin and above (i.e. anyone who is
    // not a Site Employee) can configure at least one site, so they qualify
    // for the Actions column. Per-row visibility of each menu entry is still
    // handled by the show* props passed to SiteRowActions below.
    const canConfigureAny = !isSiteEmployee;
    const showAnyAction = !isReadOnlyUser
        && (canConfigureAny || showDeleteButton || showActivationButton);

    const rowActions = showAnyAction
        ? (s: SiteAggregate) => (
            <SiteRowActions
                site={s}
                sitesAreLoading={sitesAreLoading}
                contextDealerId={contextDealerId}
                contextOrgId={contextOrgId}
                onDeleteSite={onDeleteSite}
                onLoadSites={onLoadSites}
                showConfigure={!isReadOnlyUser && !isSiteEmployee && (!siteId || siteId === s.id)}
                showDelete={!isReadOnlyUser && showDeleteButton}
                showActivation={!isReadOnlyUser && showActivationButton}
            />
        )
        : undefined;

    const isInactive = (s: SiteAggregate) => {
        if (s.activeStatus !== ActiveStatus.Active) return true;
        const ms = maintenanceModeStatus.get(s.id);
        return !!(ms?.isEnabled && ms?.endTime);
    };

    return (
        <ResponsiveDataView
            rows={sites}
            columns={columns}
            rowKey={(s) => s.id}
            actions={rowActions}
            isInactive={isInactive}
            loading={sitesAreLoading}
        />
    );
}

const useStyles = makeStyles({
    chipsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        maxWidth: '220px',
    },
    boxChip: {
        height: '22px',
        fontSize: '12px',
    },
});
